/**
 * FuelInputModal.js
 *
 * Modal with 5-button grid for quick fuel level selection.
 * Used during LET GO and ALL FAST milestones.
 * Designed for glove-friendly operation with 56px minimum tap targets.
 *
 * Features:
 * - Quick 5-button grid (Full, 3/4, 1/2, 1/4, Reserve)
 * - "Enter Exact" option for precise readings (litres, gallons, %)
 *
 * FT-284: now hosts <InlineTimeField> above the action row. `onConfirm` is
 * extended to `(fuelData, timeOptions)`. Prop names `onConfirm`/`onCancel`
 * are intentionally KEPT (rename out of scope per brief §1). Modal stays
 * open on boundary error — parent surfaces via local boundary-error state.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { X, Fuel, AlertCircle, ChevronLeft, Edit3 } from 'lucide-react';
import { FUEL_LEVEL_OPTIONS } from '../../../../../config/operationsConfig';
import { useInlineEventTime } from './useInlineEventTime';
import InlineTimeField from './InlineTimeField';
import ModalErrorBanner from './ModalErrorBanner';

// Exact value unit options
const EXACT_UNITS = [
  { value: 'percent', label: '%', suffix: '%' },
  { value: 'litres', label: 'Litres', suffix: 'L' },
  { value: 'gallons', label: 'Gallons', suffix: 'gal' }
];

const FuelInputModal = ({
  title = 'Current Fuel Level',
  subtitle = 'Select the current fuel tank level',
  onConfirm,
  onCancel,
  required = true,
  vesselTimezone
}) => {
  // FT-284: UNCONDITIONAL hook calls — Rules-of-Hooks safe.
  const timeField = useInlineEventTime(vesselTimezone);

  const [selectedLevel, setSelectedLevel] = useState(null);
  const [showError, setShowError] = useState(false);

  // Exact entry state
  const [showExactEntry, setShowExactEntry] = useState(false);
  const [exactValue, setExactValue] = useState('');
  const [exactUnit, setExactUnit] = useState('percent');

  // FT-284: boundary error + submit-in-flight guard.
  const [boundaryError, setBoundaryError] = useState(null);
  const submitInProgressRef = useRef(false);

  // FT-284: reset time field on mount; modal is always re-mounted by parent
  // (VoyageDocument toggles showFuelModal). Reset is defensive.
  useEffect(() => {
    timeField.reset();
    setBoundaryError(null);
    // timeField.reset is a stable useCallback; safe to omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = async () => {
    if (!timeField.isValid || submitInProgressRef.current) return;

    if (showExactEntry) {
      // Validate exact entry
      const numValue = parseFloat(exactValue);
      if (isNaN(numValue) || numValue < 0) {
        setShowError(true);
        return;
      }
      // For percent, enforce 0-100 range
      if (exactUnit === 'percent' && numValue > 100) {
        setShowError(true);
        return;
      }
      submitInProgressRef.current = true;
      setBoundaryError(null);
      try {
        const timeOptions = timeField.freshSnapshot();
        const result = await onConfirm(
          {
            value: numValue,
            unit: exactUnit,
            percentage: exactUnit === 'percent' ? numValue : null,
            isExact: true
          },
          timeOptions
        );
        if (result?.boundaryError) {
          setBoundaryError(result.boundaryError);
          return;
        }
        // Parent owns close on success.
      } finally {
        submitInProgressRef.current = false;
      }
    } else {
      if (required && selectedLevel === null) {
        setShowError(true);
        return;
      }
      submitInProgressRef.current = true;
      setBoundaryError(null);
      try {
        const timeOptions = timeField.freshSnapshot();
        const result = await onConfirm(
          selectedLevel !== null ? {
            value: selectedLevel,
            unit: 'percent',
            percentage: selectedLevel,
            isExact: false
          } : null,
          timeOptions
        );
        if (result?.boundaryError) {
          setBoundaryError(result.boundaryError);
          return;
        }
        // Parent owns close on success.
      } finally {
        submitInProgressRef.current = false;
      }
    }
  };

  const handleSelect = (level) => {
    setSelectedLevel(level.value);
    setShowError(false);
  };

  const handleExactValueChange = useCallback((e) => {
    const value = e.target.value;
    // Allow empty, numbers, and one decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setExactValue(value);
      setShowError(false);
    }
  }, []);

  const handleBackToQuick = useCallback(() => {
    setShowExactEntry(false);
    setExactValue('');
    setShowError(false);
  }, []);

  // Color based on fuel level
  const getLevelColor = (value, isSelected) => {
    if (isSelected) {
      if (value >= 75) return 'bg-green-600 border-green-700 ring-4 ring-green-300';
      if (value >= 50) return 'bg-cyan-600 border-cyan-700 ring-4 ring-cyan-300';
      if (value >= 25) return 'bg-amber-600 border-amber-700 ring-4 ring-amber-300';
      return 'bg-red-600 border-red-700 ring-4 ring-red-300';
    }
    if (value >= 75) return 'bg-green-500 hover:bg-green-600 border-green-600';
    if (value >= 50) return 'bg-cyan-500 hover:bg-cyan-600 border-cyan-600';
    if (value >= 25) return 'bg-amber-500 hover:bg-amber-600 border-amber-600';
    return 'bg-red-500 hover:bg-red-600 border-red-600';
  };

  // Gauge bar color
  const getGaugeColor = (level) => {
    if (level >= 75) return 'bg-green-500';
    if (level >= 50) return 'bg-cyan-500';
    if (level >= 25) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onCancel}
      />

      {/* Modal */}
      {/* FT-284: scroll containment — outer caps at 90vh, header + button row
          are siblings of the scrollable content area. */}
      <div className="relative bg-white w-full sm:w-96 sm:rounded-2xl shadow-xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-gray-900 text-white">
          <div className="flex items-center gap-2">
            <Fuel className="h-5 w-5 text-cyan-400" />
            <div>
              <h3 className="font-semibold">{title}</h3>
              <p className="text-xs text-slate-300">{subtitle}</p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="p-1 hover:bg-slate-700 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content (scrollable) */}
        <div className="p-4 flex-1 overflow-y-auto">
          {/* Error message */}
          {showError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
              <span className="text-sm text-red-700">
                {showExactEntry
                  ? exactUnit === 'percent'
                    ? 'Please enter a value between 0 and 100'
                    : 'Please enter a valid positive number'
                  : 'Please select a fuel level'}
              </span>
            </div>
          )}

          {showExactEntry ? (
            /* Exact Entry View */
            <>
              {/* Back button */}
              <button
                onClick={handleBackToQuick}
                className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-800 mb-4"
              >
                <ChevronLeft className="h-4 w-4" />
                Back to quick select
              </button>

              {/* Unit selector */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                {EXACT_UNITS.map((unit) => (
                  <button
                    key={unit.value}
                    onClick={() => setExactUnit(unit.value)}
                    className={`
                      py-3 px-4 min-h-[56px] rounded-lg border-2 font-medium transition-all
                      ${exactUnit === unit.value
                        ? 'bg-cyan-600 border-cyan-700 text-white'
                        : 'bg-white border-slate-200 text-slate-700 hover:border-cyan-300'
                      }
                    `}
                  >
                    {unit.label}
                  </button>
                ))}
              </div>

              {/* Value input */}
              <div className="mb-6">
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={exactValue}
                    onChange={handleExactValueChange}
                    placeholder="Enter value"
                    autoFocus
                    className="w-full px-4 py-4 min-h-[56px] text-2xl font-bold text-center border-2 border-slate-200 rounded-lg focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-lg text-slate-400">
                    {EXACT_UNITS.find(u => u.value === exactUnit)?.suffix}
                  </span>
                </div>
              </div>

              {/* Preview gauge for percent values */}
              {exactUnit === 'percent' && exactValue && !isNaN(parseFloat(exactValue)) && (
                <div className="mb-6">
                  <div className="text-xs text-slate-500 mb-1">Preview: {exactValue}%</div>
                  <div className="h-4 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${getGaugeColor(Math.min(parseFloat(exactValue), 100))}`}
                      style={{ width: `${Math.min(parseFloat(exactValue), 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Quick Select View */
            <>
              {/* Fuel level buttons - 5 button grid */}
              <div className="grid grid-cols-5 gap-2 mb-4">
                {FUEL_LEVEL_OPTIONS.map((level) => {
                  const isSelected = selectedLevel === level.value;
                  return (
                    <button
                      key={level.value}
                      onClick={() => handleSelect(level)}
                      className={`
                        flex flex-col items-center justify-center
                        min-h-[56px] rounded-lg border-2 transition-all
                        ${getLevelColor(level.value, isSelected)} text-white
                      `}
                    >
                      <span className="text-lg font-bold">{level.shortLabel}</span>
                      <span className="text-xs opacity-80">{level.value}%</span>
                    </button>
                  );
                })}
              </div>

              {/* Enter Exact button */}
              <button
                onClick={() => setShowExactEntry(true)}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 mb-4 border border-slate-300 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <Edit3 className="h-4 w-4" />
                <span>Enter Exact Value</span>
              </button>

              {/* Visual fuel gauge preview */}
              {selectedLevel !== null && (
                <div className="mb-4">
                  <div className="text-xs text-slate-500 mb-1">Selected: {selectedLevel}%</div>
                  <div className="h-4 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${getGaugeColor(selectedLevel)}`}
                      style={{ width: `${selectedLevel}%` }}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* FT-283 §G: inline time field + boundary banner sit just above the
            action row so they remain visible after the user scrolls. */}
        <div className="px-4 pt-3 pb-1 space-y-3 bg-white">
          {boundaryError && (
            <ModalErrorBanner message={boundaryError.humanMessage} />
          )}
          <InlineTimeField
            timeField={timeField}
            vesselTimezone={vesselTimezone}
          />
        </div>

        {/* Buttons (outside scrollable region — FT-284 scroll containment). */}
        <div className="flex gap-3 p-4 border-t border-slate-200 bg-slate-50">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-3 min-h-[56px] border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!timeField.isValid}
            className="flex-1 px-4 py-3 min-h-[56px] bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default FuelInputModal;
